import { useState, ChangeEvent } from 'react';
import { Button, Input } from 'components/atoms/index';
import { Modal } from '@/components/organisms/Modal/Modal';
import { academyAuthApi } from '@/services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';

export default function AcademyFindPassword() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix(pathname);
  const buildPath = (path: string) => (slugPrefix ? `${slugPrefix}${path}` : path);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    loginId: '',
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSendOtp = async () => {
    if (!form.loginId.trim() || !form.email.trim()) {
      await Modal.alert({ message: '아이디와 이메일을 모두 입력해주세요.' });
      return;
    }
    setSendingOtp(true);
    try {
      const res = await academyAuthApi.sendPasswordOtp({
        loginId: form.loginId.trim(),
        email: form.email.trim(),
      });
      if (res?.success) {
        setOtpSent(true);
        await Modal.alert({ message: res?.message || '인증번호를 발송했습니다.' });
      } else {
        await Modal.alert({ message: res?.message || '인증번호 발송에 실패했습니다.' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || '인증번호 발송 중 오류가 발생했습니다.';
      await Modal.alert({ message });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!form.email.trim() || !form.code.trim()) {
      await Modal.alert({ message: '이메일과 인증번호를 모두 입력해주세요.' });
      return;
    }
    setVerifyingOtp(true);
    try {
      const res = await academyAuthApi.verifyPasswordOtp({
        email: form.email.trim(),
        code: form.code.trim(),
      });
      if (res?.success) {
        setIsVerified(true);
        await Modal.alert({ message: res?.message || '인증이 완료되었습니다.' });
      } else {
        await Modal.alert({ message: res?.message || '인증번호가 올바르지 않습니다.' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || '인증확인 중 오류가 발생했습니다.';
      await Modal.alert({ message });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleProceedStep2 = async () => {
    if (!isVerified) {
      await Modal.alert({ message: '이메일 인증을 완료해주세요.' });
      return;
    }
    setStep(2);
  };

  const clearAcademySession = () => {
    try {
      localStorage.removeItem('academy_token');
      localStorage.removeItem('academy_refresh_token');
      localStorage.removeItem('academy_role');
      localStorage.removeItem('academy_name');
      localStorage.removeItem('academy_user_name');
      localStorage.removeItem('academy_login_id');
      localStorage.removeItem('academy_user_id');
      localStorage.removeItem('academy_representative_email');
    } catch {}
  };

  const handleChangePassword = async () => {
    if (!form.newPassword.trim() || !form.confirmPassword.trim()) {
      await Modal.alert({ message: '새 비밀번호를 모두 입력해주세요.' });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      await Modal.alert({ message: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await academyAuthApi.resetPassword({
        loginId: form.loginId.trim(),
        email: form.email.trim(),
        newPassword: form.newPassword,
      });
      if (res?.success) {
        await Modal.alert({ message: res?.message || '비밀번호가 변경되었습니다.', okText: '확인' });
        clearAcademySession();
        navigate(buildPath('/academy/login'), { replace: true });
      } else {
        await Modal.alert({ message: res?.message || '비밀번호 변경에 실패했습니다.' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.';
      await Modal.alert({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className='flex flex-1 justify-center my-10 w-full bg-white md:py-10 md:bg-transparent'>
      <div className='flex-1 pb-8 lg:px-10'>
        <h1 className='pb-5 heading1'>비밀번호 찾기</h1>
        {step === 1 && (
          <section>
            <div className='flex flex-col gap-5'>
              <Input
                label='아이디'
                placeholder='아이디를 입력해주세요.'
                value={form.loginId}
                onChange={handleChange('loginId')}
                disabled={sendingOtp || verifyingOtp}
              />

              <div>
                <p className='pb-1.5'>대표자 이메일</p>
                <div className='flex gap-2 items-center'>
                  <Input
                    placeholder='대표자 이메일을 입력해주세요.'
                    value={form.email}
                    onChange={handleChange('email')}
                    disabled={sendingOtp || verifyingOtp}
                  />
                  <Button className='flex-none' onClick={handleSendOtp} disabled={sendingOtp}>
                    {sendingOtp ? '발송 중...' : '인증발송'}
                  </Button>
                </div>
              </div>

              <div>
                <p className='pb-1.5'>인증번호 </p>
                <div className='flex gap-2 items-start'>
                  <div className='flex flex-col gap-1 w-full'>
                    <Input
                      placeholder='인증번호를 입력해주세요.'
                      value={form.code}
                      onChange={handleChange('code')}
                      disabled={verifyingOtp}
                    />
                    {otpSent && (
                      <small className='text-xs text-gray-500'>인증번호가 발송되었습니다.</small>
                    )}
                  </div>
                  <Button className='flex-none' onClick={handleVerifyOtp} disabled={verifyingOtp}>
                    {verifyingOtp ? '확인 중...' : '인증확인'}
                  </Button>
                </div>
              </div>
            </div>
            <Button className='mt-10 w-full' onClick={handleProceedStep2}>
              비밀번호 재설정 하기
            </Button>
          </section>
        )}

        {step === 2 && (
          <section>
            <div className='flex flex-col gap-5'>
              <Input
                label='새 비밀번호'
                placeholder='새 비밀번호를 입력해주세요.'
                type='password'
                value={form.newPassword}
                onChange={handleChange('newPassword')}
                disabled={isSubmitting}
              />
              <Input
                label='새 비밀번호 확인'
                placeholder='새 비밀번호를 다시 입력해주세요.'
                type='password'
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                disabled={isSubmitting}
              />
            </div>
            <div className='flex gap-5 mt-10'>
              <Button className='w-full' onClick={handleChangePassword} disabled={isSubmitting}>
                {isSubmitting ? '변경 중...' : '비밀번호 변경'}
              </Button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}